/**
 * 通知服务
 * 处理通知相关的API请求
 */

import api from './api';
import endpoints from './endpoints';
import { getCurrentUser } from './authService';

// 判断是否使用本地模拟数据
const VITE_USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Mock数据
const mockNotifications = [
  {
    id: 'notification-1',
    type: 'group_invitation',
    title: '团队邀请',
    content: 'AI创新团队邀请您担任技术负责人',
    data: {
      group_id: 'team-123',
      team_name: 'AI创新团队',
      invitation_id: 'invitation-1',
      role: 'tech_lead',
      inviter_id: 'user-456',
      inviter_name: '张三',
      inviter_avatar: 'https://example.com/user1.jpg'
    },
    is_read: false,
    inserted_at: '2025-08-05T15:00:00Z'
  },
  {
    id: 'notification-2',
    type: 'group_application',
    title: '入团申请',
    content: '王五申请加入您的团队担任运营经理',
    data: {
      group_id: 'team-123',
      applicant_name: '王五',
      application_id: 'application-1',
      desired_role: 'operation_manager',
      applicant_id: 'user-890',
      applicant_avatar: 'https://example.com/user3.jpg'
    },
    is_read: false,
    inserted_at: '2025-08-05T16:00:00Z'
  },
  {
    id: 'notification-3',
    type: 'community_interaction',
    title: '社区互动',
    content: '您的提案获得了新的评论',
    data: {
      proposal_id: 'proposal-1',
      proposal_title: 'AI智能助手平台',
      commenter_id: 'user-789',
      commenter_name: '李四',
      commenter_avatar: 'https://example.com/user2.jpg'
    },
    is_read: true,
    inserted_at: '2025-08-05T14:00:00Z'
  },
  {
    id: 'notification-4',
    type: 'system_announcement',
    title: '系统公告',
    content: '系统将于今晚22:00-24:00进行维护升级',
    data: {
      announcement_id: 'announcement-1',
      priority: 'medium'
    },
    is_read: false,
    inserted_at: '2025-08-05T10:00:00Z'
  }
];

const notificationService = {
  /**
   * 获取用户通知列表
   * @param {Object} params - 查询参数
   * @returns {Promise} - 通知列表数据
   */
  getNotifications: async (params = {}) => {
    try {
      // 如果使用mock数据
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据获取通知列表:', params);
        
        // 模拟延迟
        await new Promise((resolve) => setTimeout(resolve, 300));
        
        // 筛选逻辑
        let filteredNotifications = [...mockNotifications];
        
        // 按类型筛选
        if (params.type && params.type !== 'all') {
          filteredNotifications = filteredNotifications.filter(
            notification => notification.type === params.type
          );
        }
        
        // 按已读状态筛选
        if (params.is_read !== undefined) {
          filteredNotifications = filteredNotifications.filter(
            notification => notification.is_read === params.is_read
          );
        }
        
        // 分页
        const page = params.page || 1;
        const perPage = params.per_page || 20;
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);
        
        // 计算未读数量
        const unreadCount = mockNotifications.filter(n => !n.is_read).length;
        
        const mockResponse = {
          notifications: paginatedNotifications,
          pagination: {
            page,
            per_page: perPage,
            total: filteredNotifications.length,
            total_pages: Math.ceil(filteredNotifications.length / perPage)
          },
          unread_count: unreadCount
        };
        
        console.log('Mock通知列表返回:', mockResponse);
        return mockResponse;
      }

      console.log('获取通知列表，参数:', params);

      // 构建请求参数
      const requestParams = {
        page: params.page || 1,
        per_page: params.per_page || 20
      };

      if (params.category) {
        requestParams.category = params.category;
      }
      if (params.is_read !== undefined) {
        requestParams.is_read = params.is_read;
      }

      // 调用通知列表接口
      const response = await api.get(
        endpoints.notifications.list,
        { params: requestParams },
        'GetNotificationsResponse'
      );

      console.log('通知列表获取成功:', response);
      return response;
    } catch (error) {
      console.error('获取通知列表失败:', error);
      throw error;
    }
  },

  /**
   * 标记通知为已读
   * @param {string} notificationId - 通知ID
   * @returns {Promise} - 操作结果
   */
  markAsRead: async (notificationId) => {
    try {
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据标记通知已读:', notificationId);
        
        await new Promise((resolve) => setTimeout(resolve, 200));
        
        // 更新mock数据
        const notification = mockNotifications.find(n => n.id === notificationId);
        if (notification) {
          notification.is_read = true;
        }
        
        return { success: true };
      }

      console.log('标记通知已读:', notificationId);

      const response = await api.put(
        endpoints.notifications.markRead(notificationId),
        {},
        'MarkNotificationReadResponse'
      );

      console.log('标记已读成功:', response);
      return response.data || { success: true };
    } catch (error) {
      console.error('标记已读失败:', error);
      throw error;
    }
  },

  /**
   * 批量标记通知为已读
   * @param {Object} params - 参数
   * @returns {Promise} - 操作结果
   */
  markAllAsRead: async (params = {}) => {
    try {
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据批量标记已读:', params);
        
        await new Promise((resolve) => setTimeout(resolve, 300));
        
        let markedCount = 0;
        
        if (params.mark_all) {
          // 标记所有未读通知
          mockNotifications.forEach(notification => {
            if (!notification.is_read) {
              notification.is_read = true;
              markedCount++;
            }
          });
        } else if (params.notification_ids) {
          // 标记指定通知
          params.notification_ids.forEach(id => {
            const notification = mockNotifications.find(n => n.id === id);
            if (notification && !notification.is_read) {
              notification.is_read = true;
              markedCount++;
            }
          });
        }
        
        return { marked_count: markedCount };
      }

      console.log('批量标记已读:', params);

      const response = await api.put(
        endpoints.notifications.markAllRead,
        params,
        'MarkAllNotificationsReadResponse'
      );

      console.log('批量标记已读成功:', response);
      return response.data || { marked_count: 0 };
    } catch (error) {
      console.error('批量标记已读失败:', error);
      throw error;
    }
  },

  /**
   * 处理团队邀请
   * @param {string} invitationId - 邀请ID
   * @param {Object} action - 操作参数
   * @param {string} action.action - 操作类型 ('accept' | 'reject')
   * @param {string} action.reason - 拒绝理由（可选）
   * @returns {Promise} - 操作结果
   */
  handleInvitation: async (invitationId, action) => {
    try {
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据处理邀请:', invitationId, action);
        
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        // 模拟处理邀请
        const mockResponse = {
          invitation: {
            id: invitationId,
            status: action.action === 'accept' ? 'accepted' : 'rejected',
            responded_at: new Date().toISOString(),
            reason: action.reason || null // 添加拒绝理由
          }
        };
        
        if (action.action === 'accept') {
          mockResponse.team_member = {
            id: 'member-2',
            group_id: 'team-123', // 这个ID应该与通知数据中的group_id匹配
            user_id: 'user-789',
            role: 'tech_lead',
            status: 'active',
            joined_at: new Date().toISOString()
          };
        }
        
        return mockResponse;
      }

      console.log('处理团队邀请:', invitationId, action);

      const response = await api.put(
        endpoints.notifications.handleInvitation(invitationId),
        action
      );

      console.log('处理邀请成功:', response);
      return response || { invitation: null };
    } catch (error) {
      console.error('处理邀请失败:', error);
      throw error;
    }
  },

  /**
   * 处理团队申请
   * @param {string} teamId - 团队ID
   * @param {string} applicationId - 申请ID
   * @param {Object} action - 操作参数
   * @param {string} action.action - 操作类型 ('approve' | 'reject')
   * @param {string} action.role - 批准时的角色（可选）
   * @param {string} action.message - 消息（可选）
   * @param {string} action.reason - 拒绝理由（可选）
   * @returns {Promise} - 操作结果
   */
  handleApplication: async (teamId, applicationId, action) => {
    try {
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据处理申请:', teamId, applicationId, action);
        
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        // 模拟处理申请
        const mockResponse = {
          application: {
            id: applicationId,
            status: action.action === 'approve' ? 'approved' : 'rejected',
            reviewed_by: 'user-456',
            reviewed_at: new Date().toISOString(),
            reason: action.reason || null // 添加拒绝理由
          }
        };
        
        if (action.action === 'approve') {
          mockResponse.team_member = {
            id: 'member-3',
            group_id: teamId,
            user_id: 'user-890',
            role: action.role || 'member',
            status: 'active',
            joined_at: new Date().toISOString()
          };
        }
        
        return mockResponse;
      }

      console.log('处理团队申请:', teamId, applicationId, action);

      const response = await api.put(
        endpoints.notifications.handleApplication(teamId, applicationId),
        action
      );

      console.log('处理申请成功:', response);
      return response.data || { application: null };
    } catch (error) {
      console.error('处理申请失败:', error);
      throw error;
    }
  },

  /**
   * 删除通知
   * @param {string} notificationId - 通知ID
   * @returns {Promise} - 删除结果
   */
  deleteNotification: async (notificationId) => {
    try {
      if (VITE_USE_MOCK_DATA) {
        console.log('使用mock数据删除通知:', notificationId);
        
        await new Promise((resolve) => setTimeout(resolve, 300));
        
        // 模拟删除成功
        return { success: true, deleted_id: notificationId };
      }

      console.log('删除通知:', notificationId);

      const response = await api.delete(
        endpoints.notifications.delete(notificationId)
      );

      console.log('删除通知成功:', response);
      return response.data || { success: true };
    } catch (error) {
      console.error('删除通知失败:', error);
      throw error;
    }
  },

  /**
   * 获取未读通知数量
   * @returns {Promise} - 未读数量
   */
  getUnreadCount: async () => {
    try {
      if (VITE_USE_MOCK_DATA) {
        const unreadCount = mockNotifications.filter(n => !n.is_read).length;
        return { unread_count: unreadCount };
      }

      const response = await api.get(
        endpoints.notifications.unreadCount,
        {},
        'GetUnreadCountResponse'
      );
      return response.unread_count || { unread_count: 0 };
    } catch (error) {
      console.error('获取未读数量失败:', error);
      throw error;
    }
  }
};

export default notificationService;
